import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Activity, Users, TrendingUp, DollarSign,
  Building2, AlertTriangle, Cpu, Database, Server, RefreshCw, Lock,
} from "lucide-react";

/**
 * Executive Operations Dashboard — the "SOC/NOC in a page" for THOM Verify™.
 *
 * Aggregates /api/ops/metrics + /api/ops/security-score into a single
 * enterprise-ready view. Auto-refreshes every 60s.
 */
export default function ExecutiveOps() {
  const [m, setM] = useState(null);
  const [s, setS] = useState(null);
  const [tick, setTick] = useState(0);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/ops/metrics"), api.get("/ops/security-score")]);
      setM(a.data); setS(b.data); setErr(null);
    } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); const t = setInterval(() => { setTick(x => x + 1); load(); }, 60000); return () => clearInterval(t); }, []);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="kicker">Executive · Operations Centre</div>
            <h1 className="display text-4xl">Platform Health &amp; Enterprise Ops</h1>
            <p className="text-muted-foreground text-sm mt-1">Live enterprise-grade metrics across authentication, verification, escrow, revenue, and security posture. Auto-refreshes every 60 seconds.</p>
          </div>
          <div className="flex gap-2 items-center">
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Refresh #{tick + 1}</span>
            <Button variant="outline" size="sm" onClick={load} data-testid="ops-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
            <Link to="/network/intelligence"><Button variant="outline" size="sm">Network</Button></Link>
            <Link to="/geo-intelligence"><Button variant="outline" size="sm">Geo</Button></Link>
            <Link to="/admin/countries"><Button variant="outline" size="sm" data-testid="ops-country-config">Country Config</Button></Link>
            <Link to="/admin/escrow"><Button variant="outline" size="sm" data-testid="ops-escrow">Escrow</Button></Link>
            <Link to="/admin/reports"><Button variant="outline" size="sm" data-testid="ops-reports">Reports</Button></Link>
          </div>
        </div>

        {err && <div className="border border-red-500 bg-red-50 text-red-800 p-3 rounded-sm text-sm mb-6">{err}</div>}
        {!m && !err && <div className="mono text-sm text-muted-foreground">Loading metrics…</div>}

        {m && (
          <>
            {/* Platform state row */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <StatCard label="Uptime" value={`${Math.floor(m.uptime_s / 3600)}h ${Math.floor((m.uptime_s % 3600) / 60)}m`} Icon={Server} sub="current process" />
              <StatCard label="Active sessions" value={m.users.active_sessions} Icon={Users} sub={`${m.users.total} total users`} />
              <StatCard label="Verified vehicles" value={m.assets.vehicles_verified} Icon={ShieldCheck} sub={`${m.assets.vehicles_total} listings`} accent="text-primary" />
              <StatCard label="Certificates (24h)" value={m.assets.certificates_24h} Icon={Activity} sub={`${m.assets.certificates_total} total issued`} />
            </div>

            {/* Business flow row */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <StatCard label="Escrows open" value={m.escrow.open} Icon={DollarSign} accent="text-emerald-700" />
              <StatCard label="Disputes active" value={m.escrow.disputed} Icon={AlertTriangle} accent={m.escrow.disputed > 0 ? "text-amber-600" : ""} />
              <StatCard label="Trust orgs verified" value={m.trust_network.verified} Icon={Building2} sub={`${m.trust_network.organisations} total`} />
              <StatCard label="Revenue (7d USD)" value={`$${(m.revenue_7d.total_usd || 0).toLocaleString()}`} Icon={TrendingUp} sub={`${m.revenue_7d.count || 0} invoices`} />
            </div>

            {/* Security posture */}
            {s && (
              <div className="grid lg:grid-cols-[300px_1fr] gap-6 mb-8">
                <div className="border border-border p-6 bg-card text-center">
                  <div className="kicker">Security Score</div>
                  <div className={`display text-7xl mt-2 ${s.score >= 90 ? "text-primary" : s.score >= 75 ? "text-emerald-700" : s.score >= 60 ? "text-amber-600" : "text-red-600"}`}>{s.score}</div>
                  <div className="mono text-xs uppercase tracking-widest text-muted-foreground">out of 100</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm mono text-xs uppercase tracking-widest bg-primary/10 text-primary border border-primary/30">
                    <Lock className="h-3 w-3" /> {s.band}
                  </div>
                </div>
                <div className="border border-border p-6 bg-card">
                  <div className="kicker">Security checks</div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {s.checks.map(c => (
                      <div key={c.name} className="flex items-center gap-2 text-sm">
                        <span className={`h-2 w-2 rounded-full ${c.pass ? "bg-primary" : "bg-red-500"}`} />
                        <div className="flex-1">
                          <div>{c.name}</div>
                          <div className="text-[10px] mono text-muted-foreground">{c.detail}</div>
                        </div>
                        <span className="mono text-[10px] text-muted-foreground">-{c.weight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Continental Verification Intelligence (Phase 2) */}
            <ContinentalIntel />

            {/* Audit + system row */}
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="border border-border p-6 bg-card">
                <div className="kicker flex items-center gap-2"><Database className="h-3.5 w-3.5" />Audit &amp; Compliance</div>
                <div className="mt-3 space-y-2 text-sm">
                  <Row label="Audit events (24h)" value={m.audit.events_24h.toLocaleString()} />
                  <Row label="Security events (24h)" value={m.audit.security_events_24h.toLocaleString()} accent={m.audit.security_events_24h > 20 ? "text-amber-600" : ""} />
                  <Row label="Signer algorithm" value={m.signer.algo.toUpperCase()} />
                </div>
              </div>
              <div className="border border-border p-6 bg-card">
                <div className="kicker flex items-center gap-2"><Cpu className="h-3.5 w-3.5" />Trust Network Activity</div>
                <div className="mt-3 space-y-2 text-sm">
                  <Row label="Trusted updates (24h)" value={m.trust_network.trusted_updates_24h} />
                  <Row label="Passport events (24h)" value={m.assets.passport_events_24h} />
                  <Row label="New users (24h)" value={m.users.new_24h} />
                </div>
              </div>
              <div className="border border-border p-6 bg-card">
                <div className="kicker flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" />Recommendations</div>
                <ul className="mt-3 space-y-2 text-sm">
                  {(s?.recommendations || []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2"><span className="mono text-xs text-primary mt-0.5">→</span><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

const StatCard = ({ label, value, Icon, sub, accent }) => (
  <div className="border border-border p-4 bg-card">
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker">{label}</div>
        <div className={`display text-3xl mt-1 ${accent || ""}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
  </div>
);

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className={`mono ${accent || ""}`}>{value}</span>
  </div>
);

// ─── Continental Verification Intelligence (Executive Ops · Phase 2) ────
import { Globe, DollarSign as _DS, AlertTriangle as _AT, Clock, ShieldCheck as _SC, MapPin } from "lucide-react";

function ContinentalIntel() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => {
    try { const r = await api.get("/ops/executive"); setD(r.data); } catch {}
  })(); }, []);
  if (!d) return null;
  const k = d.kpis || {};
  const maxCountry = Math.max(1, ...(d.country_breakdown || []).map(c => c.total));
  const maxStage = Math.max(1, ...(d.stage_distribution || []).map(s => s.count));
  return (
    <div className="mt-8" data-testid="ops-continental">
      <div className="flex items-end justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="kicker">Continental verification intelligence</div>
          <h2 className="text-xl font-semibold mt-0.5">Executive Operations Centre · Africa</h2>
        </div>
        <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Refreshed {new Date(d.generated_at).toLocaleTimeString()}</span>
      </div>

      {/* KPI grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Requests today" value={k.requests_today} Icon={Globe} />
        <KpiCard label="Active verifications" value={k.active_verifications} Icon={Users} />
        <KpiCard label="Completed today" value={k.completed_today} Icon={_SC} accent="text-emerald-700" />
        <KpiCard label="Success rate" value={`${k.verification_success_rate || 0}%`} Icon={_SC} />
        <KpiCard label="Revenue pipeline" value={`$${(k.revenue_pipeline_usd || 0).toLocaleString()}`} Icon={_DS} accent="text-primary" />
        <KpiCard label="Escrow value held" value={`$${(k.escrow_held_usd || 0).toLocaleString()}`} Icon={_DS} />
        <KpiCard label="Avg turnaround" value={`${k.avg_turnaround_hours || 0}h`} Icon={Clock} />
        <KpiCard label="Countries active" value={k.countries_active} Icon={MapPin} />
      </div>

      {/* Country breakdown + Stage distribution */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="border border-border bg-card p-5">
          <div className="kicker flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />Country breakdown</div>
          <div className="mt-3 space-y-2">
            {(d.country_breakdown || []).slice(0, 10).map(c => (
              <div key={c.country} data-testid={`ops-country-${c.country}`}>
                <Link
                  to={`/admin/verification-requests?country=${encodeURIComponent(c.country)}`}
                  className="block hover:bg-muted/30 rounded-sm px-1 py-0.5 transition-colors"
                  data-testid={`ops-country-drill-${c.country}`}
                >
                  <div className="flex items-center justify-between text-[12.5px]">
                    <span className="font-medium">{c.country}</span>
                    <span className="mono text-[10.5px] text-muted-foreground">{c.total} total · {c.active} active · {c.high_urgency} priority</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${(c.total / maxCountry) * 100}%` }} />
                  </div>
                </Link>
              </div>
            ))}
            {(d.country_breakdown || []).length === 0 && <div className="text-xs text-muted-foreground">No country data yet.</div>}
          </div>
        </div>
        <div className="border border-border bg-card p-5">
          <div className="kicker">Pipeline distribution</div>
          <div className="mt-3 space-y-2">
            {(d.stage_distribution || []).map(s => (
              <div key={s.stage} data-testid={`ops-stage-${s.stage}`}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="capitalize">{s.stage.replace(/_/g, ' ')}</span>
                  <span className="mono text-[10.5px] text-muted-foreground">{s.count}</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${(s.count / maxStage) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live feed + High risk alerts */}
      <div className="grid lg:grid-cols-[2fr_1fr] gap-4 mb-2">
        <div className="border border-border bg-card p-5">
          <div className="kicker">Live activity feed</div>
          <ol className="mt-3 space-y-1.5 max-h-[280px] overflow-y-auto">
            {(d.live_feed || []).slice(0, 15).map((f, i) => (
              <li key={i} className="text-[11.5px] border-l-2 border-primary/40 pl-2" data-testid={`ops-feed-${i}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="mono text-[10.5px] text-primary">{f.reference}</span>
                  <span className="mono text-[9.5px] text-muted-foreground">{new Date(f.at || Date.now()).toLocaleString()}</span>
                </div>
                <div className="text-[11.5px]">{f.country} · {f.category || '—'} <span className="mono text-[9.5px] uppercase text-muted-foreground">{f.status}</span></div>
              </li>
            ))}
            {(d.live_feed || []).length === 0 && <li className="text-xs text-muted-foreground">No recent activity.</li>}
          </ol>
        </div>
        <div className="border border-amber-500/30 bg-amber-500/[0.03] p-5">
          <div className="kicker flex items-center gap-2 text-amber-700"><_AT className="h-3.5 w-3.5" />High-risk alerts</div>
          <ol className="mt-3 space-y-1.5">
            {(d.high_risk_alerts || []).map((r, i) => (
              <li key={i} className="text-[11.5px] border-l-2 border-amber-500 pl-2" data-testid={`ops-risk-${i}`}>
                <div className="mono text-[10.5px] text-amber-700">{r.reference}</div>
                <div>{r.country} · <span className="mono uppercase text-[9.5px]">{r.urgency}</span> · overdue &gt;24h</div>
              </li>
            ))}
            {(d.high_risk_alerts || []).length === 0 && <li className="text-xs text-muted-foreground">No high-risk alerts.</li>}
          </ol>
        </div>
      </div>
    </div>
  );
}

const KpiCard = ({ label, value, Icon, accent }) => (
  <div className="border border-border bg-card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker">{label}</div>
        <div className={`display text-2xl mt-1 ${accent || ""}`}>{value}</div>
      </div>
      {Icon && <Icon className="h-4 w-4 text-primary" />}
    </div>
  </div>
);
